import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Map } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import {
  EmptyState,
  HeaderCount,
  SectionCard,
  SectionToolbar,
  TableShell,
  Th,
} from "../components/ui";
import { CreateMzOrderModal } from "../components/mz/CreateMzOrderModal";
import { MzStatusBadge } from "../components/mz/MzStatusBadge";
import { useMzOrders } from "../hooks";
import { fmtDate } from "../utils";
import type { MzOrderStatus } from "../types";

const STATUS_FILTERS: { value: "" | "active" | MzOrderStatus; label: string }[] = [
  { value: "active", label: "Aktywne" },
  { value: "", label: "Wszystkie" },
  { value: "fee_paid", label: "Do wysyłki" },
  { value: "sent", label: "Wysłane" },
  { value: "received", label: "Otrzymane" },
  { value: "completed", label: "Zakończone" },
];

// Module-level list of mapa zasadnicza orders. One order = one ZE project — the
// scope is the project's MDCP outline, requested as vector .dxf (formularz P).
/* Zakładka „Mapy MZ" strony „Zamawianie materiałów" (/power-projects/zamawianie?tab=mz). */
export function MzSection() {
  const me = useAuthStore((s) => s.user);
  const { data: orders, isLoading } = useMzOrders();

  const [createModal, setCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"" | "active" | MzOrderStatus>("active");
  const [mineOnly, setMineOnly] = useState(false);

  const paidCount = useMemo(
    () => (orders ?? []).filter((o) => o.status === "fee_paid").length,
    [orders],
  );

  const visibleOrders = useMemo(() => {
    let rows = orders ?? [];
    if (statusFilter === "active") {
      rows = rows.filter((o) => !["completed", "cancelled"].includes(o.status));
    } else if (statusFilter) {
      rows = rows.filter((o) => o.status === statusFilter);
    }
    // „Moje" = założone przeze mnie albo przejęte po nieaktywnym autorze.
    if (mineOnly && me?.id) {
      rows = rows.filter(
        (o) => String(o.created_by) === String(me.id) || String(o.taken_over_by) === String(me.id),
      );
    }
    return rows;
  }, [orders, statusFilter, mineOnly, me?.id]);

  return (
    <div className="space-y-4">
      <SectionToolbar
        text="Mapa zasadnicza w wektorze (.dxf) na obrys MDCP projektu — formularz P + załącznik DXF."
        actions={<HeaderCount count={paidCount} label="do wysyłki" tone="amber" />}
      />

      <SectionCard
        title="Zamówienia map zasadniczych"
        action={
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input type="checkbox" checked={mineOnly} onChange={() => setMineOnly((v) => !v)} />
              moje
            </label>
            <select
              className="input-base !py-0.5 text-xs"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <Button size="sm" onClick={() => setCreateModal(true)}>
              <Map size={13} className="mr-1" /> Zamów mapę
            </Button>
          </div>
        }
      >
        {isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : !visibleOrders.length ? (
          <EmptyState>Brak zamówień — „Zamów mapę” tworzy wniosek na obrys MDCP projektu.</EmptyState>
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Numer</Th>
                <Th>Projekt ZE</Th>
                <Th>Powiat</Th>
                {/* Z którego obrysu MDCP wniosek bierze zakres — projekt ma ich wiele. */}
                <Th>Obrys</Th>
                <Th>Skala</Th>
                <Th>Status</Th>
                <Th>Zamawiający</Th>
                <Th>Utworzono</Th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((o) => (
                <tr
                  key={o.id}
                  className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] ${
                    o.status === "fee_paid" ? "bg-amber-50/60" : ""
                  }`}
                >
                  <td className="px-2 py-1.5 font-mono">
                    <Link to={`/power-projects/mz/${o.id}`} className="hover:underline font-bold">
                      {o.number}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">
                    <Link to={`/power-projects/${o.project_id}`} className="hover:underline">
                      {o.project_name}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">{o.county || "—"}</td>
                  <td className="px-2 py-1.5">{o.buffer_name || "—"}</td>
                  <td className="px-2 py-1.5 font-mono">{o.scale}</td>
                  <td className="px-2 py-1.5"><MzStatusBadge status={o.status} /></td>
                  <td className="px-2 py-1.5">{o.created_by_name}</td>
                  <td className="px-2 py-1.5">{fmtDate(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </SectionCard>

      {createModal && <CreateMzOrderModal onClose={() => setCreateModal(false)} />}
    </div>
  );
}
